<template>
  <teleport to="head">
    <MetaTags
      :title="`DNS records for ${domain}`"
      :description="`These are the uncached A, AAAA, CNAME, TXT, NS, MX and SOA records for ${domain}. Other DNS records are also available.`"
      :url="`https://www.nslookup.io/domains/${domainPunycode}/dns-records/`"
    />
  </teleport>
  <HeaderComponent :domain="domain" />
  <div class="flex-grow bg-neutral-100">
    <main class="container mx-auto max-w-6xl">
      <h1 class="text-3xl mt-8 ml-6">DNS records for <b v-if="domain === domainPunycode">{{ domain }}</b><span v-else><b>{{ domainPunycode }}</b> ({{ domain }})</span></h1>
      <a v-if="showUsersearchAd()" class="inline-block mt-1 ml-6 text-gray-600 font-medium text-sm hover:text-indigo-600" href="https://usersearch.org/landing/index.html" rel="noopener">Find domains by this owner</a>
      <div class="sticky z-10 mt-6 top-0 bg-neutral-100">
        <nav class="pl-6 flex gap-7 items-center md:gap-8" aria-label="Tabs">
          <router-link v-for="tab in tabs" :key="tab.name" :to="`#${tab.name}`" :aria-current="isCurrentTab(tab.name) ? 'page' : undefined"
                       :class="[isCurrentTab(tab.name) ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300',
                                ['google', 'opendns', 'authoritative'].includes(tab.name) ? 'hidden sm:inline' : '',
                                'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm']">{{ tab.display }}</router-link>
          <Menu as="div" class="hidden sm:inline-block relative text-left">
            <div>
              <MenuButton :class="[
                isCurrentTab('controld') ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300',
                'inline-flex w-full justify-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-transparent',
              ]">
                Control D
                <ChevronDownIcon class="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
              </MenuButton>
            </div>

            <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
              <MenuItems class="absolute -right-16 z-10 mt-2 w-44 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:right-0">
                <div class="py-1">
                  <router-link v-for="queryTarget in controlDQueryTargest" :key="queryTarget.name" :to="`#${queryTarget.name}`">
                    <MenuItem v-slot="{ active }">
                      <span :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', isCurrentTab(queryTarget.name) ? '!text-indigo-600 font-medium' : '', 'block pl-6 pr-4 py-2 text-sm']">{{ queryTarget.display }}</span>
                    </MenuItem>
                  </router-link>
                  <a href="https://controld.com/?cid=nslookup" target="_blank" rel="noopener nereferrer">
                    <MenuItem v-slot="{ active }">
                      <span :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex justify-between pl-6 pr-4 py-2 text-sm']">
                        Custom
                        <ArrowTopRightOnSquareIcon class="w-4 float-right" />
                      </span>
                    </MenuItem>
                  </a>
                </div>
              </MenuItems>
            </transition>
          </Menu>
          <Menu as="div" class="relative inline-block text-left">
            <div>
              <MenuButton :class="[
                isCurrentTab('cloudflare') ? 'border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300' : 'border-indigo-500 text-indigo-600',
                !isCurrentTab('cloudflare') && !isCurrentTab('local') ? 'sm:border-transparent sm:text-gray-600 sm:hover:text-gray-700 sm:hover:border-gray-300' : '',
                'inline-flex w-full justify-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-transparent',
              ]">
                <span class="inline sm:hidden">Other</span>
                <span class="hidden sm:inline">Local DNS</span>
                <ChevronDownIcon class="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
              </MenuButton>
            </div>

            <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
              <MenuItems class="absolute -right-16 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:right-0">
                <div class="py-1">
                  <span class="block px-4 mt-2 text-sm font-semibold text-gray-500 sm:hidden">Global</span>
                  <router-link v-for="queryTarget in tabs.slice(1)" :key="queryTarget.name" :to="`#${queryTarget.name}`" class="block sm:hidden">
                    <MenuItem v-slot="{ active }">
                      <span :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', isCurrentTab(queryTarget.name) ? '!text-indigo-600 font-medium' : '', 'block pl-6 pr-4 py-2 text-sm']">{{ queryTarget.display }}</span>
                    </MenuItem>
                  </router-link>
                  <template v-for="(queryTargets, name) in localQueryTargets" :key="name">
                    <span class="block px-4 mt-2 text-sm font-semibold text-gray-500">{{ name }}</span>
                    <router-link v-for="queryTarget in queryTargets" :key="queryTarget.name" :to="`#${queryTarget.name}`">
                      <MenuItem v-slot="{ active }">
                        <span :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', isCurrentTab(queryTarget.name) ? '!text-indigo-600 font-medium' : '', 'block pl-6 pr-4 py-2 text-sm']">{{ queryTarget.display }}</span>
                      </MenuItem>
                    </router-link>
                  </template>
                </div>
              </MenuItems>
            </transition>
          </Menu>
          <div class="grow">
            <Menu as="div" class="ml-auto w-6 mr-4 flex">
              <MenuButton>
                <Cog6ToothIcon class="h-6 w-6 text-gray-600" aria-hidden="true" />
              </MenuButton>

              <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                <MenuItems class="absolute right-12 top-3 z-10 p-3 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <ToggleRawOutput>Show raw data</ToggleRawOutput>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </nav>
      </div>
      <div class="bg-white shadow p-6 pb-10 rounded-t">
        <ApiErrorAlert v-if="error !== null" :error="error" :domain="domain" :dnsserver="dnsserver" />
        <template v-else>
          <p v-if="dnsServers[dnsserver].type === 'local'">A local DNS server in {{ dnsServers[dnsserver].pretty }}
            responded with these DNS records. It will serve these records for as long as the time to live (TTL) has
            not expired. After this period, the DNS server will update its cache by querying one of the
            authoritative name servers.</p>
          <p v-else-if="dnsServers[dnsserver].type === 'popular'">The {{ dnsServers[dnsserver].pretty }} DNS server
            responded with these DNS records. {{ dnsServers[dnsserver].pretty }} will serve these records for as
            long as the time to live (TTL) has not expired. After this period, {{ dnsServers[dnsserver].pretty }}
            will update its cache by querying one of the authoritative name servers.</p>
          <template v-else-if="dnsServers[dnsserver].type === 'control d'">
            <p class="mb-2">The {{ dnsServers[dnsserver].pretty }} DNS server
              responded with these DNS records. {{ dnsServers[dnsserver].pretty }} will serve these records for as
              long as the time to live (TTL) has not expired. After this period, {{ dnsServers[dnsserver].pretty }}
              will update its cache by querying one of the authoritative name servers.</p>
            <p class="mt-2">
              <span v-if="dnsserver == 'controldunfiltered'">This DNS server does not filter requests. </span>
              <span v-else-if="dnsserver == 'controldmalware'">This DNS server filters malware. </span>
              <span v-else-if="dnsserver == 'controldadstracking'">This DNS server filters ads and trackers. </span>
              <span v-else-if="dnsserver == 'controldsocial'">This DNS server filters social media. </span>
              <span v-else-if="dnsserver == 'controldfamilyfriendly'">This DNS server filters adult and drugs related sites. </span>
              <span v-else-if="dnsserver == 'controlduncesored'">This DNS server restores access to censored sites. </span>
              <a href="https://controld.com/free-dns?cid=nslookup" class="text-blue-600 hover:text-blue-800 hover:underline" target="_blank" rel="noopener noreferrer">Configure this server on your device.</a>
            </p>
          </template>
          <p v-else>The authoritative DNS server responded with these DNS records. It has indicated that records
            should be cached for the period specified by the time to live (TTL). When the configuration of the
            authoritative DNS server changes, it will immediately serve the updated DNS records.</p>
          <div class="flex gap-6">
            <div v-if="loading" class="shrink w-full lg:max-w-2xl">
              <div v-for="n in 2" :key="n">
                <SkeletonComponent class="w-56 h-8 mb-6 mt-8" />
                <div v-for="n in 2" :key="n">
                  <SkeletonComponent v-for="n in 2" :key="n" class="inline-block w-1/2 h-4 pr-[10%] bg-clip-content" />
                </div>
              </div>
            </div>
            <div v-else class="shrink w-full lg:max-w-2xl">
              <h2 class="mt-8 mb-2 font-semibold text-2xl">A records</h2>
              <ip-dns-records :records="records.a" column-title="IPv4 address" record-type="A" />
              <h2 class="mt-8 mb-2 font-semibold text-2xl">AAAA records</h2>
              <ip-dns-records :records="records.aaaa" column-title="IPv6 address" record-type="AAAA" />
              <h2 class="mt-8 mb-2 font-semibold text-2xl">CNAME record</h2>
              <dns-records :records="records.cname" column-title="Canonical name" record-type="CNAME" :is-domain="true" :can-have-multiple-records="false" class="w-full" />
            </div>
            <div v-if="showSidebar" class="grow w-[330px] mt-10 hidden lg:block">
              <AdUnit class="!sticky top-16 mx-auto" :config="DnsRecordsSidebar" />
            </div>
          </div>
          <div v-if="loading" aria-hidden="true" class="mb-8">
            <div v-for="n in 3" :key="n">
              <SkeletonComponent class="w-56 h-8 mb-6 mt-8" />
              <div v-for="n in 3" :key="n">
                <SkeletonComponent v-for="n in 3" :key="n" class="inline-block w-1/3 h-4 pr-[10%] bg-clip-content" />
              </div>
            </div>
          </div>
          <div v-else>
            <h2 class="mt-8 mb-2 font-semibold text-2xl">TXT records</h2>
            <txt-records :records="records.txt" :domain="domain" class="w-full" />
            <AdUnit class="mt-8 mx-auto" :config="DnsRecordsTxt" />
            <h2 class="mt-8 mb-2 font-semibold text-2xl">NS records</h2>
            <ns-records :records="records.ns" :domain="domain" class="w-full" />
            <h2 class="mt-8 mb-2 font-semibold text-2xl">MX records</h2>
            <mx-records :records="records.mx" class="w-full" />
          </div>
          <div v-if="loading" aria-hidden="true" class="mt-6 mb-8">
            <SkeletonComponent class="w-56 h-8 mb-6 mt-8" />
          </div>
          <template v-else>
            <h2 class="mt-8 mb-4 font-semibold text-2xl inline-block">Other records</h2>
            <Combobox as="div" v-model="otherRecordType" class="mb-3 sm:mb-0 sm:mt-8 sm:ml-10 sm:inline-block">
              <ComboboxLabel class="sr-only">Additional record type</ComboboxLabel>
              <div class="relative">
                <ComboboxInput @change="otherRecordTypeQuery = $event.target.value" @focus="otherRecordTypeQuery = ''" class="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm" />
                <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                  <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                </ComboboxButton>

                <ComboboxOptions v-if="filteredOtherRecordTypes.length > 0" class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <ComboboxOption v-for="person in filteredOtherRecordTypes" :key="person" :value="person" as="template" v-slot="{ active, selected }">
                    <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-indigo-600 text-white' : 'text-gray-900']">
                      <span :class="['block truncate', selected && 'font-semibold']">
                        {{ person }}
                      </span>

                      <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-indigo-600']">
                        <CheckIcon class="h-5 w-5" aria-hidden="true" />
                      </span>
                    </li>
                  </ComboboxOption>
                </ComboboxOptions>
              </div>
            </Combobox>
          </template>
          <div v-if="loading || loadingOther" aria-hidden="true" class="mt-6 mb-8">
            <div v-for="n in 3" :key="n">
              <SkeletonComponent v-for="n in 3" :key="n" class="inline-block w-1/3 h-4 pr-[10%] bg-clip-content" />
            </div>
          </div>
          <template v-else>
            <soa-records v-if="otherRecordType === 'SOA'" :records="records.soa" :domain="domain" class="w-full" />
            <caa-records v-else-if="otherRecordType === 'CAA'" :records="otherRecords" :domain="domain" class="w-full" />
            <ptr-records v-else-if="otherRecordType === 'PTR'" :records="otherRecords" :domain="domain" class="w-full" />
            <srv-records v-else-if="otherRecordType === 'SRV'" :records="otherRecords" :domain="domain" class="w-full" />
            <tlsa-records v-else-if="otherRecordType === 'TLSA'" :records="otherRecords" :domain="domain" class="w-full" />
            <dns-records v-else :records="otherRecords" column-title="Data" :record-type="otherRecordType" class="w-full" />
          </template>
          <AdUnit class="mt-8 mx-auto" :config="DnsRecordsOther" />
          <h2 class="mt-8 mb-2 font-semibold text-2xl">Look up DNS records for another domain name</h2>
          <div class="flex flex-col lg:flex-row">
            <p class="shrink max-w-xl">With <router-link :to="`/`" class="text-blue-600 hover:text-blue-800 hover:underline">DNS lookup</router-link>, you can find the DNS
              record for any domain name or subdomain. When you enter the domain, it will show all the DNS records that are configured for that domain.</p>
            <form class="mt-3 w-[500px] max-w-full sm:px-6 lg:mt-0" autocomplete="off">
              <DomainSearch emphasize :prefill="domain" @submitted="redirectTo" />
            </form>
          </div>
        </template>
      </div>
    </main>
  </div>
  <FooterComponent />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios, { Canceler } from 'axios';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/vue';
import {
  CheckIcon,
  ChevronUpDownIcon,
  ChevronDownIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/vue/20/solid';
import { Cog6ToothIcon } from '@heroicons/vue/24/outline';
import HeaderComponent from '@/components/layout/HeaderComponent.vue';
import FooterComponent from '@/components/layout/FooterComponent.vue';
import DnsRecords from '@/components/records/DnsRecords.vue';
import IpDnsRecords from '@/components/records/IpDnsRecords.vue';
import SoaRecords from '@/components/records/SoaRecords.vue';
import TxtRecords from '@/components/records/TxtRecords.vue';
import MxRecords from '@/components/records/MxRecords.vue';
import ToggleRawOutput from '@/components/ToggleRawOutput.vue';
import NsRecords from '@/components/records/NsRecords.vue';
import MetaTags from '@/components/MetaTags.vue';
import CaaRecords from '@/components/records/CaaRecords.vue';
import PtrRecords from '@/components/records/PtrRecords.vue';
import SrvRecords from '@/components/records/SrvRecords.vue';
import TlsaRecords from '@/components/records/TlsaRecords.vue';
import { domainNameToDisplayName, domainNameToUrlPath, urlPathToDomainName } from '@/common/domainName';
import SkeletonComponent from '@/components/micro/SkeletonComponent.vue';
import DomainSearch from '@/components/DomainSearch.vue';
import { ApiErrorType, toErrorType } from '@/model/ApiErrorAlert';
import ApiErrorAlert from '@/components/ApiErrorAlert.vue';
import { dnsServers } from '@/common/dnsServers';
import {
  GetRecordsResponse, PopularRecords, QueryResult, emptyPopularRecords, emptyQueryResult,
} from '@/model/Api';
import AdUnit from '@/components/ads/AdUnit.vue';
import { DnsRecordsOther, DnsRecordsSidebar, DnsRecordsTxt } from '@/common/adSlots';
import { showUsersearchAd } from '@/common/dateTime';

const props = defineProps({
  routerDomain: {
    type: String,
    required: true,
  },
});
const route = useRoute();
const router = useRouter();

const tabs = [
  { name: 'cloudflare', display: 'Cloudflare' },
  { name: 'google', display: 'Google DNS' },
  { name: 'authoritative', display: 'Authoritative' },
];
const controlDQueryTargest = [
  { name: 'controldunfiltered', display: 'Unfiltered' },
  { name: 'controldmalware', display: 'Malware' },
  { name: 'controldadstracking', display: 'Ads & Tracking' },
  { name: 'controldsocial', display: 'Social' },
  { name: 'controldfamilyfriendly', display: 'Family Friendly' },
];
const localQueryTargets = {
  Africa: [
    { name: 'southafrica', display: 'South Africa' },
  ],
  Australia: [
    { name: 'australia', display: 'Australia' },
  ],
  Asia: [
    { name: 'india', display: 'India' },
    { name: 'russia', display: 'Russia' },
  ],
  Europe: [
    { name: 'thenetherlands', display: 'The Netherlands' },
    { name: 'ukraine', display: 'Ukraine' },
  ],
  'North America': [
    { name: 'canada', display: 'Canada' },
    { name: 'usa', display: 'United States of America' },
  ],
  'South America': [
    { name: 'brazil', display: 'Brazil' },
  ],
};

const otherRecordType = ref('SOA');
const otherRecordTypes = ['AFSDB', 'APL', 'CAA', 'CDNSKEY', 'CDS', 'CERT', 'CSYNC', 'DHCID', 'DLV', 'DNAME', 'DNSKEY', 'DS', 'HINFO', 'HIP', 'IPSECKEY', 'IXFR', 'KEY', 'KX', 'LOC', 'NAPTR', 'NSEC', 'NSEC3', 'NSEC3PARAM', 'OPENPGPKEY', 'OPT', 'PTR', 'RP', 'RRSIG', 'SIG', 'SMIMEA', 'SOA', 'SRV', 'SSHFP', 'TA', 'TKEY', 'TLSA', 'TSIG', 'URI', 'ZONEMD'];
const otherRecordTypeQuery = ref('SOA');
const filteredOtherRecordTypes = computed(() => (otherRecordTypeQuery.value === ''
  ? otherRecordTypes
  : otherRecordTypes.filter((item) => item.toLowerCase().includes(otherRecordTypeQuery.value.toLowerCase()))));

const domain = ref('');
const domainPunycode = ref('');
const dnsserver = ref(route.hash.substring(1));
const loadingOther = ref(true);
const loading = ref(true);
const error = ref<ApiErrorType | null>(null);
const showSidebar = ref(window.innerWidth > 991);
const cancelToken = ref<Canceler | null>(null);
const cancelTokenOtherRecords = ref<Canceler | null>(null);
const records = ref<PopularRecords>(emptyPopularRecords);
const otherRecords = ref<QueryResult>(emptyQueryResult);

function isCurrentTab(tab: string) {
  if (tab === 'local') {
    return Object.values(localQueryTargets).some((queryTargets) => queryTargets.some((target) => target.name === dnsserver.value));
  }
  if (tab === 'controld') {
    return Object.values(controlDQueryTargest).some((target) => target.name === dnsserver.value);
  }
  return tab === dnsserver.value;
}

function loadOtherRecord(): void {
  loadingOther.value = true;
  otherRecords.value = emptyQueryResult;

  // Cancel any previous request
  if (cancelTokenOtherRecords.value) {
    cancelTokenOtherRecords.value();
  }
  const axiosSource = axios.CancelToken.source();
  cancelTokenOtherRecords.value = axiosSource.cancel;

  axios.post('api/v1/other-records', {
    domain: domain.value,
    dnsServer: dnsserver.value,
    recordType: otherRecordType.value,
  }, {
    cancelToken: axiosSource.token,
  })
    .then((response) => {
      otherRecords.value = response.data.queryResult;
      loadingOther.value = false;
    })
    .catch((e) => {
      if (!axios.isCancel(e)) {
        // eslint-disable-next-line
        console.log(e.message);
      }
    });
}

function load() {
  if (dnsserver.value === '') {
    dnsserver.value = 'cloudflare';
  }
  loading.value = true;
  error.value = null;
  records.value = emptyPopularRecords;

  if (cancelToken.value) {
    cancelToken.value();
  }
  const axiosSource = axios.CancelToken.source();
  cancelToken.value = axiosSource.cancel;

  axios.post<GetRecordsResponse>('api/v1/records', {
    domain: domain.value,
    dnsServer: dnsserver.value,
  }, {
    cancelToken: axiosSource.token,
  })
    .then((response) => {
      if (props.routerDomain !== domainNameToUrlPath(response.data.punycodeDomain)) {
        router.replace({
          path: `/domains/${domainNameToUrlPath(response.data.punycodeDomain)}/dns-records/`,
          hash: route.hash,
        });
      }
      domain.value = domainNameToDisplayName(response.data.unicodeDomain);
      domainPunycode.value = domainNameToDisplayName(response.data.punycodeDomain);
      records.value = response.data.records;
      if (otherRecordType.value === 'SOA') {
        loadingOther.value = false;
      }
      loading.value = false;
    })
    .catch((e) => {
      if (!axios.isCancel(e)) {
        error.value = toErrorType(e.response.data.error);
      }
    });

  if (otherRecordType.value === 'SOA') {
    loadingOther.value = true;
  } else {
    loadOtherRecord();
  }
}

function redirectTo(newDomain: string): void {
  router.push(`/domains/${domainNameToUrlPath(newDomain)}/dns-records/`);
}

watch(() => otherRecordType.value, () => loadOtherRecord());

watch(() => props.routerDomain, () => {
  const domainName = urlPathToDomainName(props.routerDomain);
  domain.value = domainName;
  domainPunycode.value = domainName;
  load();
}, {
  immediate: true,
});

watch(() => route.hash, () => {
  if (route.name !== 'Dns records') {
    return; // Prevent loading DNS records when moving to another page
  }

  dnsserver.value = route.hash.substring(1);
  load();
});

window.addEventListener('resize', () => {
  showSidebar.value = window.innerWidth > 991;
});
</script>
